import {
  Activity,
  BarChart3,
  Briefcase,
  Building2,
  CalendarRange,
  FileSearch,
  Flag,
  Gauge,
  LayoutList,
  MapPin,
  Package,
  ScrollText,
  SlidersHorizontal,
  Tag,
  UserRound,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import type { ProductKey } from "./hooks";

/**
 * Which product a page works on:
 * - "switch": the product picked in the top bar (the page reloads when it changes)
 * - a ProductKey: always that product; the top bar switch doesn't apply
 * - "both": both products at once (shared data, or each row says its product)
 */
export type PageScope = "switch" | ProductKey | "both";

export type NavItem = { to: string; label: string; icon: LucideIcon; scope: PageScope };
export type NavGroup = { label: string; items: NavItem[] };

export const NAV_GROUPS: NavGroup[] = [
  {
    label: "Overview",
    items: [
      { to: "/admin/dashboard", label: "Dashboard", icon: Gauge, scope: "switch" },
      { to: "/admin/analytics", label: "Analytics", icon: BarChart3, scope: "switch" },
      { to: "/admin/audit-logs", label: "Audit log", icon: ScrollText, scope: "both" },
    ],
  },
  {
    label: "Release controls",
    items: [
      { to: "/admin/feature-flags", label: "Feature flags", icon: Flag, scope: "both" },
      { to: "/admin/theresume-menu", label: "theResume menu", icon: LayoutList, scope: "THERESUME" },
      { to: "/admin/features", label: "Features & limits", icon: SlidersHorizontal, scope: "switch" },
    ],
  },
  {
    label: "Pricing",
    items: [
      { to: "/admin/pricing/plans", label: "Plans", icon: Wallet, scope: "switch" },
      { to: "/admin/pricing/packs", label: "Packs", icon: Package, scope: "switch" },
      { to: "/admin/pricing/coupons", label: "Coupons", icon: Tag, scope: "switch" },
    ],
  },
  {
    label: "Customers",
    items: [
      { to: "/admin/users", label: "Candidates", icon: UserRound, scope: "switch" },
      { to: "/admin/recruiters", label: "Recruiters", icon: Users, scope: "THERECRUIT" },
      { to: "/admin/companies", label: "Companies", icon: Building2, scope: "THERECRUIT" },
      { to: "/admin/crm-leads", label: "CRM leads", icon: Activity, scope: "both" },
    ],
  },
  {
    label: "Jobs & data",
    items: [
      { to: "/admin/jobs", label: "Jobs", icon: Briefcase, scope: "THERECRUIT" },
      { to: "/admin/location-backfill", label: "Location backfill", icon: MapPin, scope: "both" },
      {
        to: "/admin/experience-dates-backfill",
        label: "Experience dates backfill",
        icon: CalendarRange,
        scope: "both",
      },
      { to: "/admin/resume-pdf-audit", label: "Resume PDF audit", icon: FileSearch, scope: "THERESUME" },
    ],
  },
];

export const PRODUCT_NAME: Record<ProductKey, string> = {
  THERECRUIT: "theRecruit",
  THERESUME: "theResume",
};

/** The nav entry (and its group) a path belongs to; detail pages belong to their list page. */
export const pageFor = (pathname: string): { group: NavGroup; item: NavItem } | undefined => {
  const path = pathname.replace(/\/+$/, "");
  if (path === "/admin") return pageFor("/admin/dashboard");
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (path === item.to || path.startsWith(`${item.to}/`)) return { group, item };
    }
  }
  return undefined;
};

/** What the top bar says about a page's product. */
export const describeScope = (scope: PageScope): string =>
  scope === "switch"
    ? "Follows the product switch"
    : scope === "both"
    ? "Both products"
    : `${PRODUCT_NAME[scope]} only`;
